import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Avatar, Box, Grid, Typography, Stack, Tooltip, IconButton, LinearProgress, Chip, Fade } from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import HotelIcon from '@mui/icons-material/Hotel';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MainCard from 'ui-component/cards/MainCard';
import TarjetaEsqueletoOrden from 'ui-component/cards/Skeleton/EarningCard';

// ==============================|| COMPONENTES ESTILIZADOS ||============================== //

const TarjetaProfesional = styled(MainCard)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: '#fff',
  overflow: 'hidden',
  position: 'relative',
  border: 'none',
  borderRadius: '12px',
  boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  minHeight: '160px',
  maxHeight: '160px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px 0 rgba(0, 0, 0, 0.15)'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 80%, ${theme.palette.primary.light}30 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, ${theme.palette.primary.dark}30 0%, transparent 50%)`,
    pointerEvents: 'none'
  },
  '& > div': {
    position: 'relative',
    zIndex: 5
  }
}));

const ChipEstadisticas = styled(Chip)(({ theme, trend }) => ({
  height: 24,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: trend === 'up' ? 'rgba(76, 175, 80, 0.15)' : trend === 'down' ? 'rgba(244, 67, 54, 0.15)' : 'rgba(255, 255, 255, 0.15)',
  color: trend === 'up' ? '#4caf50' : trend === 'down' ? '#f44336' : 'rgba(255, 255, 255, 0.9)',
  '& .MuiChip-icon': {
    fontSize: '0.875rem',
    color: 'inherit'
  }
}));

const BotonInfo = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 4,
  right: 4,
  color: 'rgba(255, 255, 255, 0.7)',
  padding: 2,
  '&:hover': {
    color: 'rgba(255, 255, 255, 1)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)'
  }
}));

// ==============================|| UTILIDADES ||============================== //

const calcularEstadisticas = (datosHoteles) => {
  if (!datosHoteles || datosHoteles.length === 0) {
    return {
      total: 0,
      nuevosEsteMes: 0,
      hotelesActivos: 0,
      tasaCrecimiento: 0,
      tendencia: 'neutral',
      capacidadTotal: 0
    };
  }

  const ahora = new Date();
  const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
  const inicioMesAnterior = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);

  // Calcular nuevos hoteles este mes
  const nuevosEsteMes = datosHoteles.filter((hotel) => {
    const fechaCreacion = new Date(hotel.createdDate || hotel.createdAt);
    return fechaCreacion >= inicioMes && !isNaN(fechaCreacion);
  }).length;

  // Calcular hoteles del mes pasado
  const hotelesMesAnterior = datosHoteles.filter((hotel) => {
    const fechaCreacion = new Date(hotel.createdDate || hotel.createdAt);
    return fechaCreacion >= inicioMesAnterior && fechaCreacion < inicioMes && !isNaN(fechaCreacion);
  }).length;

  // Calcular tasa de crecimiento
  const tasaCrecimiento =
    hotelesMesAnterior > 0 ? (((nuevosEsteMes - hotelesMesAnterior) / hotelesMesAnterior) * 100).toFixed(1) : nuevosEsteMes > 0 ? 100 : 0;

  // Calcular hoteles activos (con habitaciones disponibles o estado activo)
  const hotelesActivos = datosHoteles.filter((hotel) => {
    const tieneHabitaciones = hotel.rooms && hotel.rooms > 0;
    const estaActivo = hotel.status === 'active' || hotel.isActive === true;
    const noEstaInactivo = hotel.status !== 'inactive' && hotel.isActive !== false;

    return tieneHabitaciones || estaActivo || noEstaInactivo;
  }).length;

  // Calcular capacidad total de habitaciones
  const capacidadTotal = datosHoteles.reduce((total, hotel) => {
    return total + (hotel.rooms || 0);
  }, 0);

  return {
    total: datosHoteles.length,
    nuevosEsteMes,
    hotelesActivos,
    tasaCrecimiento: parseFloat(tasaCrecimiento),
    tendencia: tasaCrecimiento > 0 ? 'up' : tasaCrecimiento < 0 ? 'down' : 'neutral',
    capacidadTotal
  };
};

// ==============================|| COMPONENTE PRINCIPAL ||============================== //

const TarjetaTotalHoteles = ({ isLoading, hotelData }) => {
  const theme = useTheme();
  const [estadisticas, setEstadisticas] = useState({
    total: 0,
    nuevosEsteMes: 0,
    hotelesActivos: 0,
    tasaCrecimiento: 0,
    tendencia: 'neutral',
    capacidadTotal: 0
  });
  const [mostrarDetalles, setMostrarDetalles] = useState(false);

  useEffect(() => {
    if (hotelData) {
      setEstadisticas(calcularEstadisticas(hotelData));
    }
  }, [hotelData]);

  const porcentajeActivos = estadisticas.total > 0 ? ((estadisticas.hotelesActivos / estadisticas.total) * 100).toFixed(1) : 0;

  if (isLoading) {
    return <TarjetaEsqueletoOrden />;
  }

  return (
    <Fade in={true} timeout={500}>
      <TarjetaProfesional border={false} content={false}>
        <Box sx={{ p: 2, position: 'relative', height: '100%' }}>
          {/* Botón de información */}
          <Tooltip title="Detalles de hoteles">
            <BotonInfo size="small" onClick={() => setMostrarDetalles(!mostrarDetalles)}>
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </BotonInfo>
          </Tooltip>

          <Grid container spacing={1} height="100%">
            {/* Sección Principal */}
            <Grid item xs={12}>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Box flex={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontWeight: 400,
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      mb: 0.5
                    }}
                  >
                    Total de Hoteles
                  </Typography>

                  <Stack direction="row" alignItems="baseline" spacing={1}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        fontSize: '2rem',
                        lineHeight: 1
                      }}
                    >
                      {estadisticas.total}
                    </Typography>

                    {estadisticas.tendencia !== 'neutral' && (
                      <ChipEstadisticas
                        icon={estadisticas.tendencia === 'up' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                        label={`${Math.abs(estadisticas.tasaCrecimiento)}%`}
                        size="small"
                        trend={estadisticas.tendencia}
                      />
                    )}
                  </Stack>

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      mt: 0.5,
                      fontSize: '0.75rem',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <AddBusinessIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    {estadisticas.nuevosEsteMes} nuevos este mes
                  </Typography>
                </Box>

                {/* Avatar/Icono */}
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <HotelIcon sx={{ fontSize: 22 }} />
                </Avatar>
              </Stack>
            </Grid>

            {/* Sección de Estadísticas Adicionales */}
            <Grid item xs={12}>
              <Box sx={{ mt: 1 }}>
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '0.7rem'
                      }}
                    >
                      Hoteles Activos
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    >
                      {estadisticas.hotelesActivos} ({porcentajeActivos}%)
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(porcentajeActivos)}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 2,
                        backgroundColor: '#fff'
                      }
                    }}
                  />
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </TarjetaProfesional>
    </Fade>
  );
};

TarjetaTotalHoteles.propTypes = {
  isLoading: PropTypes.bool,
  hotelData: PropTypes.array
};

export default TarjetaTotalHoteles;
